import {
	DateString,
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { useDataInputDialog, useUserTimezone } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";

type AnonymousLinkUpdateConfirmationDialogProps = {
	expirationDate?: string | Date;
	onResolve: (result: AnonymousLinkUpdateResult) => void;
	onClose: () => void;
};

const AnonymousLinkUpdateConfirmationDialog = (props: AnonymousLinkUpdateConfirmationDialogProps): ReactElement => {
	const {
		expirationDate,
		onResolve,
		onClose
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	return (
		<Dialog trackingId="sp-share-link-confirmation-dialog">
			<Dialog.Header
				title={localize("_UpdateLinkQuestionMark")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<span>
					{isNil(expirationDate)
						? localize("_SimilarLinkExistsWithoutExpirationDate")
						: (
							<FormattedText
								text={localize("_SimilarLinkExistsWithExpirationDate")}
								thingsToReplace={[
									{
										what: "{0}",
										filler: (
											<DateString
												value={expirationDate}
												format="DMY"
												timezone={userTimezone}
											/>
										)
									}
								]}
							/>
						)
					}
				</span>
				<span>
					{localize("_DoYouWantToKeepExistingShareLinkSettingsOrUpdateWithTheNewSettings")}
				</span>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_UseNewSettings")}
						onClick={() => onResolve(AnonymousLinkUpdateResult.UseNewSettings)}
						trackingId="user-new"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_UseExistingSettings")}
						onClick={() => onResolve(AnonymousLinkUpdateResult.UseExistingSettings)}
						trackingId="user-existing"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AnonymousLinkUpdateConfirmationDialog.displayName = "AnonymousLinkUpdateConfirmationDialog";

export enum AnonymousLinkUpdateResult {
	UseNewSettings = 1,
	UseExistingSettings = 2
}

export type AnonymousLinkUpdateConfirmationDialogInitialData = Omit<
	AnonymousLinkUpdateConfirmationDialogProps,
	"onClose" | "onResolve"
>;

export function useAnonymousLinkUpdateConfirmationDialog(): [
	element: ReactElement | null,
	open: (props: AnonymousLinkUpdateConfirmationDialogInitialData) => Promise<TryResult<AnonymousLinkUpdateResult>>
] {
	return useDataInputDialog(
		(props, onClose, onResolve) => (
			<AnonymousLinkUpdateConfirmationDialog
				{...props}
				onResolve={onResolve}
				onClose={onClose}
			/>
		)
	);
}
